import React, { useState, useEffect } from 'react';
import {
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
  COMMAND_PRIORITY_EDITOR
} from 'lexical';
import DropDown, { DropDownItem } from '../../../ui/DropDown';
import { Divider } from '@chakra-ui/react';

export const ELEMENT_FORMAT_OPTIONS: {
  [key in Exclude<ElementFormatType, ''>]: {
    icon: string;
    iconRTL: string;
    name: string;
  };
} = {
  center: {
    icon: 'center-align',
    iconRTL: 'center-align',
    name: 'Center Align'
  },
  end: {
    icon: 'right-align',
    iconRTL: 'left-align',
    name: 'End Align'
  },
  justify: {
    icon: 'justify-align',
    iconRTL: 'justify-align',
    name: 'Justify Align'
  },
  left: {
    icon: 'left-align',
    iconRTL: 'left-align',
    name: 'Left Align'
  },
  right: {
    icon: 'right-align',
    iconRTL: 'right-align',
    name: 'Right Align'
  },
  start: {
    icon: 'left-align',
    iconRTL: 'right-align',
    name: 'Start Align'
  }
};

interface ElementFormatDropdownProps {
  editor: LexicalEditor;
  value: ElementFormatType;
  isRTL: boolean;
  disabled: boolean;
}

export function ElementFormatDropdown({
  editor,
  value,
  isRTL,
  disabled
}: ElementFormatDropdownProps) {
  const [currentFormat, setCurrentFormat] = useState('');
  const formatOption = ELEMENT_FORMAT_OPTIONS[currentFormat || value || 'left'];

  useEffect(() => {
    setCurrentFormat(value);
  }, [value]);

  editor.registerCommand(
    FORMAT_ELEMENT_COMMAND,
    (payload) => {
      value = payload;
      setCurrentFormat(payload);
      return false;
    },
    COMMAND_PRIORITY_EDITOR
  );

  return (
    <DropDown
      disabled={disabled}
      buttonLabel={formatOption.name}
      buttonIconClassName={`icon ${isRTL ? formatOption.iconRTL : formatOption.icon}`}
      buttonClassName="toolbar-item spaced alignment"
      buttonAriaLabel="Formatting options for text alignment"
    >
      {Object.entries(ELEMENT_FORMAT_OPTIONS).map(([format, option]) => (
        <DropDownItem
          key={format}
          onClick={() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format as ElementFormatType);
            setCurrentFormat(format);
          }}
          className="item"
        >
          <i className={`icon ${isRTL ? option.iconRTL : option.icon}`} />
          <span className="text">{option.name}</span>
        </DropDownItem>
      ))}
      <Divider />
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        }}
        className="item"
      >
        <i className={'icon ' + (isRTL ? 'indent' : 'outdent')} />
        <span className="text">Outdent</span>
      </DropDownItem>
      <DropDownItem
        onClick={() => {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        }}
        className="item"
      >
        <i className={'icon ' + (isRTL ? 'outdent' : 'indent')} />
        <span className="text">Indent</span>
      </DropDownItem>
    </DropDown>
  );
}
